import { Checkbox, FormControlLabel, TextField } from "@mui/material";
import clsx from "clsx";
import debounce from "lodash.debounce";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { ExternalLink } from "../../../common/components/ExternalLink";
import { InlineLoading } from "../../../common/components/Loading";
import { useActionRequest } from "../../../common/hooks/api";
import { useDataMap } from "../../../common/hooks/api/useDataMap";
import { Api } from "../../../common/service/Api";
import { useDataMapDisclosedPICGroups } from "../../../common/service/resources/data-map/dataMapTransformers";
import { CollectionGroupDetailsDto } from "../../../common/service/server/dto/CollectionGroupDto";
import { DataMapDto } from "../../../common/service/server/dto/datamap/DataMapDto";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { RequestHandlingInstructionDto } from "../../../common/service/server/dto/RequestHandlingInstructionDto";
import {
  OrganizationPublicId,
  RequestHandlingInstructionType,
  UUIDString,
} from "../../../common/service/server/Types";
import { Disableable } from "../../components";
import { Callout, CalloutVariant } from "../../components/Callout";
import { DataRecipientLogo } from "../../components/DataRecipientLogo";
import { HelpPopover } from "../../components/HelpPopover";
import { StandardLabel } from "../../components/input/Labels";
import { PageHeader } from "../../components/layout/header/Header";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { PicGroupList } from "../../components/organisms/collection-groups/PicGroupList";
import { RequestInstructionsProgressFooter } from "../../components/requestInstructions";
import { REQUEST_HANDLING_INSTRUCTIONS_MAX_LENGTH } from "./constants";
import { RequestHandlingInstructionsLengthWarning } from "./RequestHandlingInstructionsTextarea";
import { Para } from "../../components/typography/Para";
import { Paras } from "../../components/typography/Paras";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { useRequestInstructions } from "../../hooks/useOrganizationVendors";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { useManagedAdNetworks } from "../../../common/hooks/dataRecipientSettingsHooks";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../get-compliant/data-recipients/shared";
import { getVendorNesting } from "../../util/vendorNesting";
import { useDataRetrievedVia } from "../../hooks/useManagedAdNetworks";
import { useAvailableIntegrationVendorIds } from "../../hooks/useAvailableIntegrationVendorIds";
import { canBeAccessDataSource } from "../../util/vendors";
import { Routes } from "../../root/routes";
import { alphabetically } from "../../surveys/util";
import { VENDOR_CATEGORY_AD_NETWORK } from "../../types/Vendor";
import { GetCompliantContainer } from "../get-compliant/GetCompliantContainer";

const requestType = "KNOW" as RequestHandlingInstructionType;

interface HeaderProps {
  consumerGroups?: CollectionGroupDetailsDto[];
}
const KnowInstructionsHeader: React.FC<HeaderProps> = () => {
  const description = (
    <Paras>
      <Para>
        When you receive a Request to Know, you’ll send the requester all of the data you have about
        them, whether stored internally or in your vendor’s systems.
      </Para>
      <Para>
        Write instructions to help your team retrieve the requester’s personal data from each
        accessible system.
      </Para>
    </Paras>
  );

  return (
    <PageHeader
      titleHeaderContent="Request Instructions"
      titleContent="Manage Request to Know Settings"
      descriptionContent={description}
      wide={true}
    />
  );
};

export const KnowInstructionsPage = () => {
  // Constants

  // State
  const history = useHistory();
  const [instructions, setInstructions] = useState<null | RequestHandlingInstructionDto[]>(null);

  // Requests
  const [organization, orgRequest] = usePrimaryOrganization();
  const [vendors, vendorsRequest] = useOrganizationVendors(organization.id, undefined, true);
  const { data: dataMap, request: dataMapRequest } = useDataMap(organization.id);
  const [, instructionsRequest] = useRequestInstructions(
    organization.id,
    requestType,
    setInstructions,
  );

  const { answers: sellingAndSharingAnswers } = useSurvey(
    organization.id,
    SELLING_AND_SHARING_SURVEY_NAME,
  );
  const { managedAdNetworks } = useManagedAdNetworks(organization.id);
  const { data: dataRetrievedVia, isError: dataRetrievedViaFailed } = useDataRetrievedVia(
    organization.id,
  );
  const { integrationAvailability, isReady: integrationAvailabilityReady } =
    useAvailableIntegrationVendorIds(true, { failFast: true });
  // Wait for the links (and which vendors' Access data is reachable) so a linked vendor doesn't show as its own card
  // first; if the links fail to load, every vendor keeps its own card.
  const nestingReady =
    (dataRetrievedVia !== undefined || dataRetrievedViaFailed) && integrationAvailabilityReady;

  // Recipients handled by a managing vendor are configured through that manager, so they are not listed separately
  // here - matching the Access workflow step and the Delete/Opt-Out instructions pages (POL-7958). The same goes for
  // recipients whose Access data is retrieved via another recipient (DES-43), under the same source rule as Access
  // setup. This page edits "not stored" locally, so the rule reads this page's instructions rather than saved ones.
  const nesting = useMemo(
    () =>
      getVendorNesting(vendors, managedAdNetworks, sellingAndSharingAnswers, {
        dataRetrievedVia,
        isDataRetrievedViaEligible: (vendor) =>
          canBeAccessDataSource(
            vendor,
            integrationAvailability,
            instructions?.find(
              (instruction) =>
                instruction.vendorId === vendor.vendorId && instruction.requestType === requestType,
            ),
          ),
      }),
    [
      vendors,
      managedAdNetworks,
      sellingAndSharingAnswers,
      dataRetrievedVia,
      integrationAvailability,
      instructions,
    ],
  );

  const sortedVendors = useMemo(() => {
    return (
      vendors?.sort(alphabetically)?.filter((v) => !v.isInstalled && !nesting.isManaged(v.id)) ?? []
    );
  }, [vendors, nesting]);

  const onDone = () => {
    history.push(Routes.organization.requestHandling.base);
  };

  // Content
  return (
    <StandardPageWrapper
      requests={[orgRequest, vendorsRequest, instructionsRequest, dataMapRequest]}
      footer={
        <RequestInstructionsProgressFooter
          nextContent="Save"
          nextDisabled={false}
          nextOnClick={onDone}
          currentRequest={null}
        />
      }
    >
      {organization && (
        <>
          <KnowInstructionsHeader />

          <GetCompliantContainer className="request-instructions" wide={true}>
            {nestingReady ? (
              <VendorsPanel
                orgId={organization.id}
                vendors={sortedVendors}
                instructions={instructions}
                setInstructions={setInstructions}
                dataMap={dataMap}
                getManagedVendorsForManager={nesting.getManagedVendorsForManager}
                getRetrievedViaVendorsForManager={nesting.getRetrievedViaVendorsForManager}
              />
            ) : (
              <InlineLoading />
            )}
          </GetCompliantContainer>
        </>
      )}
    </StandardPageWrapper>
  );
};

const debouncedFn = debounce((fn, ...args) => fn(...args), 500);

// TODO: checkboxize, not the copied radio
const NotStoredCheckbox = ({ onChange, method, disabled }) => {
  const onCheckboxChange = (e) => {
    onChange(e.target.checked);
  };

  const checked = method == "INACCESSIBLE_OR_NOT_STORED";

  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            onChange={onCheckboxChange}
            checked={checked}
            value="INACCESSIBLE_OR_NOT_STORED"
            disabled={disabled}
          />
        }
        className={"inaccessible"}
        label="Data is inaccessible or not stored "
      />
      <HelpPopover label="Select if the system does not store data, or the data is inaccessible (e.g., routinely deleted within 30 days.)" />
    </>
  );
};

// TODO: everything below here should get probably moved to Components

const matchInstruction = (inst: RequestHandlingInstructionDto, orgId, vendorId, requestType) => {
  return (
    orgId === inst.organizationId && vendorId === inst.vendorId && requestType == inst.requestType
  );
};

const findInstruction = (
  instructions: null | RequestHandlingInstructionDto[],
  orgId: OrganizationPublicId,
  vendorId: UUIDString,
) => {
  return instructions?.find((inst) => matchInstruction(inst, orgId, vendorId, requestType));
};

type VendorCardProps = {
  orgId: any;
  instructions: null | RequestHandlingInstructionDto[];
  setInstructions: any;
  vendor: OrganizationDataRecipientDto;
  index: number; // Gross, stop using utility classes for styles
  dataMap: DataMapDto;
  /** Recipients this vendor handles requests for; they are hidden from the list and named here instead (POL-7958). */
  managedVendors?: OrganizationDataRecipientDto[];
  /** Recipients whose data is retrieved via this vendor (DES-43); hidden from the list the same way. */
  retrievedViaVendors?: OrganizationDataRecipientDto[];
};

const VendorCard: React.FC<VendorCardProps> = ({
  orgId,
  vendor,
  instructions,
  setInstructions,
  index,
  dataMap,
  managedVendors = [],
  retrievedViaVendors = [],
}) => {
  const baseInstruction = {
    organizationId: orgId,
    requestType: requestType,
    vendorId: vendor.vendorId,
  } as RequestHandlingInstructionDto;
  const disclosedPicGroups = useDataMapDisclosedPICGroups(dataMap, {
    exclude: { collectionGroupType: "EMPLOYMENT" },
  });

  const foundInstruction = findInstruction(instructions, orgId, vendor.vendorId);

  // State
  const [instruction, setInstruction] = useState<RequestHandlingInstructionDto>(
    foundInstruction || baseInstruction,
  );

  useEffect(() => {
    if (instructions) {
      setInstruction(foundInstruction || baseInstruction);
    }
  }, [Boolean(instructions)]);

  const [instructionText, setInstructionText] = useState("");

  const updateInstruction = (newInstruction) => {
    setInstruction(newInstruction);

    // update the locally stored instructions
    const clonedInstructions = instructions ? [...instructions] : [];

    const idx = instructions?.indexOf(foundInstruction) ?? -1;

    if (idx >= 0) {
      clonedInstructions[idx] = newInstruction;
    } else {
      clonedInstructions.push(newInstruction);
    }

    setInstructions(clonedInstructions);

    updateRemoteInstruction(newInstruction);
  };

  // Network
  const { fetch: updateRemoteInstruction } = useActionRequest({
    api: (newInstruction) => Api.requestHandlingInstructions.putInstruction(orgId, newInstruction),
  });

  // Callbacks
  const setIsInaccessible = (isInaccessible: boolean) => {
    updateInstruction({
      ...instruction,
      processingMethod: isInaccessible ? "INACCESSIBLE_OR_NOT_STORED" : null,
    });
  };

  useEffect(() => {
    if (instruction?.processingInstructions) {
      setInstructionText(instruction.processingInstructions);
    }
  }, [instruction]);

  const handleTextChange = ({ target }) => {
    setInstructionText(target.value);
    debouncedFn(updateInstruction, { ...instruction, processingInstructions: target.value });
  };

  const vendorHeader = (
    <div className="vendor-instructions--container">
      <DataRecipientLogo dataRecipient={vendor} size={64} />
      <div className="vendor-instructions--vendor-info">
        <h4 className="intermediate my-0">{vendor.name}</h4>
        {managedVendors.length > 0 && (
          <div className="text-gray-600 text-xs font-medium">
            Managing: {managedVendors.map((managed) => managed.name).join(", ")}
          </div>
        )}
        {retrievedViaVendors.length > 0 && (
          <div className="text-gray-600 text-xs font-medium">
            Also includes data from: {retrievedViaVendors.map((nested) => nested.name).join(", ")}
          </div>
        )}
        {vendor.ccpaRequestToKnowLink && (
          <div className="classify-vendors--classification-details">
            <ExternalLink
              href={vendor.ccpaRequestToKnowLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {vendor.name}’s retrieval instructions
            </ExternalLink>
          </div>
        )}
      </div>
    </div>
  );

  const processingInstructions = (
    <>
      <StandardLabel label="Processing instructions" subLabel=" (optional)" />

      <TextField
        className="instructions--text-field"
        variant="outlined"
        value={instructionText}
        onChange={handleTextChange}
        inputProps={{ maxLength: REQUEST_HANDLING_INSTRUCTIONS_MAX_LENGTH }}
        fullWidth={false}
        multiline={true}
        rows={4}
      />
      <RequestHandlingInstructionsLengthWarning valueLength={instructionText.length} />
    </>
  );

  const inUnselectedAdNetworks =
    vendor.category == VENDOR_CATEGORY_AD_NETWORK && vendor.usesCustomAudience !== true;

  const instructionsVisible =
    instruction && instruction.processingMethod !== "INACCESSIBLE_OR_NOT_STORED";

  const notStoredDisabled =
    instruction?.processingMethod !== "INACCESSIBLE_OR_NOT_STORED" &&
    Boolean(vendor.accessInstructions) &&
    !inUnselectedAdNetworks;

  return (
    <div
      key={vendor.vendorId}
      className={clsx("paper-divider-bottom", { "py-xl": !!index, "pb-xl": !index })}
    >
      {vendorHeader}
      <div className="mb-md">
        <Disableable
          disabled={notStoredDisabled}
          disabledTooltip="Data is accessible via the instructions shown below."
        >
          <NotStoredCheckbox
            onChange={setIsInaccessible}
            method={instruction?.processingMethod}
            disabled={notStoredDisabled}
          />
        </Disableable>
      </div>
      {Boolean(disclosedPicGroups.length) && (
        <Callout
          variant={CalloutVariant.Clear}
          className="mb-md"
          cardContentClassName="pt-0"
          title={`Data stored in ${vendor.name}`}
        >
          <PicGroupList picGroups={disclosedPicGroups} />
        </Callout>
      )}
      {Boolean(vendor.accessInstructions) && (
        <Callout
          variant={CalloutVariant.Gray}
          title="Access Instructions"
          className={"mb-md"}
          __dangerousChildrenHtml={vendor.accessInstructions}
        />
      )}

      {instructionsVisible && processingInstructions}
    </div>
  );
};

type VendorsPanelProps = {
  orgId: any;
  vendors: OrganizationDataRecipientDto[];
  instructions: null | RequestHandlingInstructionDto[];
  setInstructions: any;
  dataMap: DataMapDto;
  getManagedVendorsForManager: (dataRecipientId: string) => OrganizationDataRecipientDto[];
  getRetrievedViaVendorsForManager: (dataRecipientId: string) => OrganizationDataRecipientDto[];
};

const VendorsPanel: React.FC<VendorsPanelProps> = ({
  orgId,
  vendors,
  instructions,
  setInstructions,
  dataMap,
  getManagedVendorsForManager,
  getRetrievedViaVendorsForManager,
}) => {
  return (
    <div>
      {vendors.map((vendor, index) => {
        return (
          <VendorCard
            key={vendor.vendorId}
            orgId={orgId}
            vendor={vendor}
            instructions={instructions}
            setInstructions={setInstructions}
            index={index}
            dataMap={dataMap}
            managedVendors={getManagedVendorsForManager(vendor.id)}
            retrievedViaVendors={getRetrievedViaVendorsForManager(vendor.id)}
          />
        );
      })}
    </div>
  );
};
